'use client';

import React, { useState, useMemo } from 'react';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';

interface TermEntry {
  term: string;
  definition: string;
  category: string;
}

export default function ElevatorTerminologyContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLetter, setSelectedLetter] = useState('all');

  // All terminology entries
  const terminologyData: TermEntry[] = [
    {
      term: 'Access Device',
      definition: 'A special tool used to disengage landing door locks – from landing side of the elevator hoistway.',
      category: 'Safety'
    },
    {
      term: 'Access Door',
      definition: 'Doors which provide access to machine rooms and overhead machine spaces. These are kept locked at all times.',
      category: 'Safety'
    },
    {
      term: 'Alarm Bell',
      definition: 'Mounted on top of the car and operated by a push button on the car station panel.',
      category: 'Safety'
    },
    {
      term: 'Automatic Operation',
      definition: 'The starting of the elevator car in response to the momentary actuation of operating devices at the landing and/or of operating devices in the car or at a landing. Once the device is actuated the car will stop automatically at the landing and the doors open.',
      category: 'Operation'
    },
    {
      term: 'Battery Backup',
      definition: 'An emergency power source that will allow you to lower the elevator to the next lower level in the event of a power failure.',
      category: 'Electrical'
    },
    {
      term: 'Brake',
      definition: 'An electro-mechanical device used to prevent the elevator from moving when the car is at rest and no power is applied to the hoist-motor. On some types of control it also brings the car to a stop when power is removed from the hoist-motor.',
      category: 'Structural'
    },
    {
      term: 'Brushes',
      definition: 'A conducting device, usually made of carbon or graphite composition, used to connect a circuit with the rotating or moving portion (commutator) of a D.C. motor or generator.',
      category: 'Electrical'
    },
    {
      term: 'Buffer',
      definition: 'This is a device that was designed in order to stop a car from descending below its normal travel limit. It does this by storing or absorbing the kinetic energy of the car and then dissipating it elsewhere.',
      category: 'Safety'
    },
    {
      term: 'Call',
      definition: 'A demand for elevator service placed in an elevator signal system. The signal may be placed from either the car station or landing push button.',
      category: 'Operation'
    },
    {
      term: 'Call Button',
      definition: 'Pressing the call button in the hallway will call the elevator to your level.',
      category: 'Operation'
    },
    {
      term: 'Capacity',
      definition: 'The maximum weight that can be accommodated by the elevator.',
      category: 'Structural'
    },
    {
      term: 'Car (or Cab)',
      definition: 'The elevator car transports passengers from one floor to another. These come pre-finished or can be left unfinished to accept your design.',
      category: 'Structural'
    },
    {
      term: 'Car Frame',
      definition: 'The supporting frame to which the car platform, car superstructure, guide shoes, car safety gear, and the hoisting ropes or hoisting rope sheaves, or the hydraulic elevator ram are attached.',
      category: 'Structural'
    },
    {
      term: 'Gate Switch',
      definition: 'A safety switch operated by car door when doors are in their fully closed position. One of several car safety switches.',
      category: 'Safety'
    },
    {
      term: 'Car Operating Panel (COP), Car Station',
      definition: 'A panel, mounted in the car, containing car operating controls such as call register (floor) pushbuttons, alarm, emergency stop, and any other buttons or key switches that may be required for operation.',
      category: 'Operation'
    },
    {
      term: 'Car Position Indicator',
      definition: 'Mounted within the elevator car to indicate to passengers the car\'s floor position. Normally located above the door jamb these can consist of a row of illuminating numbers or a digital display.',
      category: 'Operation'
    },
    {
      term: 'Car Station',
      definition: 'A panel mounted in the car containing the elevator operating control buttons, such as floor designations, door open and close, alarm, emergency stop and other buttons or key operated switches as required. If two panels are fitted one is termed the main panel, the other is the auxiliary.',
      category: 'Operation'
    },
    {
      term: 'Car-Top Inspection Station',
      definition: 'A control panel on top of an elevator car which, when activated, removes the car from normal service and allows the car to run at inspection speed from the car top station only.',
      category: 'Safety'
    },
    {
      term: 'C.O.P.',
      definition: 'The Car Operating Panel (C.O.P.) is the control panel inside the elevator that houses the floor buttons, the light switch, the alarm button, the emergency stop switch, and optional key lock.',
      category: 'Operation'
    },
    {
      term: 'Compensating Chain',
      definition: 'This part of the elevator is a welded link chain that is used as a weight compensation. One end is attached to beneath the elevator while the other is attached to the counterweight.',
      category: 'Structural'
    },
    {
      term: 'Controller',
      definition: 'An electrical panel which performs many computer functions by which it operates an elevator.',
      category: 'Electrical'
    },
    {
      term: 'Counterweight',
      definition: 'Added weight on a traction elevator to provide a balance to the car.',
      category: 'Structural'
    },
    {
      term: 'Counterweight Guard',
      definition: 'A sheet metal screen to prevent a person from standing under the counterweight as it descends into the pit area.',
      category: 'Safety'
    },
    {
      term: 'Drive System',
      definition: 'The drive system is the power and strength behind lifting the elevator car and its passenger(s).',
      category: 'Structural'
    },
    {
      term: 'Dumbwaiter',
      definition: 'Is a self-contained car that is lowered and raised on a vertical path. Dumbwaiters can carry loads from 50 lbs. to 500 lbs. depending on the model chosen. Load examples may be fireplace wood, grocery bags, laundry, and paperwork.',
      category: 'Structural'
    },
    {
      term: 'Duplex operation',
      definition: 'A type of operation in which two elevators operate from common landing buttons through a common control system.',
      category: 'Operation'
    },
    {
      term: 'Electromechanical Interlock',
      definition: 'Often referred to as EMI, is an electromechanical safety lock that prevents the hoistway door (hall door) from opening if the elevator is not at that landing.',
      category: 'Safety'
    },
    {
      term: 'Escutcheon',
      definition: 'The hole in the hall door, usually near the top of the door.',
      category: 'Structural'
    },
    {
      term: 'Existing Installation',
      definition: 'Term used when there is an existing elevator. Usually used when someone is going to replace one elevator with another elevator.',
      category: 'Operation'
    },
    {
      term: 'FPM (Feet Per Minute)',
      definition: 'The rate the elevator runs.',
      category: 'Operation'
    },
    {
      term: 'Final Limit',
      definition: 'One of two mechanically operated switches mounted in an elevator hoistway, one at the top and one at the bottom, which if activated by the car, traveling more than a preset distance beyond a terminal landing, cuts off power to the elevator drive motor.',
      category: 'Safety'
    },
    {
      term: 'Freight Elevator',
      definition: 'This is a specific type of elevator that is used in order to carry materials and machinery rather than people.',
      category: 'Structural'
    },
    {
      term: 'Gate',
      definition: 'The gate is a door that attaches to the outer edge of the elevator or dumbwaiter car. It prevents objects inside the car from coming into contact with objects outside of the car during travel. Gate styles include: accordion for elevators and bi-part and roll-top gates for dumbwaiters.',
      category: 'Safety'
    },
    {
      term: 'Geared Traction Machine',
      definition: 'A traction hoist-machine in which the power from the motor is transmitted to the drive or traction sheave through a reduction gearbox.',
      category: 'Structural'
    },
    {
      term: 'Gearless Traction Machine',
      definition: 'A traction hoist-machine in which the motor directly drives the traction sheave which forms an integral part of the motor armature.',
      category: 'Structural'
    },
    {
      term: 'Generator',
      definition: 'An electromechanical device that converts mechanical energy to electrical energy (usually direct current) used to supply variable voltage and thus speed control to a D.C hoist motor.',
      category: 'Electrical'
    },
    {
      term: 'Governor',
      definition: 'A mechanical speed control mechanism. The governor monitors the speed of the car in the down direction by using one rope. If the car travels too fast in the down direction, the governor is tripped and a set of safeties are engaged to stop the car.',
      category: 'Safety'
    },
    {
      term: 'Guide Rails',
      definition: 'Steel "T" shaped sections with machined guiding surfaces. These are installed vertically in an elevator hoistway to guide and direct the course of travel of an elevator car and its counterweight.',
      category: 'Structural'
    },
    {
      term: 'Guide Shoes',
      definition: 'The devices located between the car or counterweight and their respective guide rails to ensure that the lateral motion of the car and counterweight is kept at a minimum as they travel along the guide rails.',
      category: 'Structural'
    },
    {
      term: 'Hall Chime',
      definition: 'An electronic tone generator and speaker located within the hall lantern. It emits a dual tone to announce to waiting passengers the imminent arrival of an elevator car. Earlier types used an electromechanical gong separated from the hall lantern. Tones typically sound once for an elevator travelling in the up direction and twice for the down direction.',
      category: 'Operation'
    },
    {
      term: 'Hall Lantern',
      definition: 'An illuminated direction sign installed on each landing. The illuminated area usually forms an arrow indicating that the elevator is approaching and the direction in which it is to travel.',
      category: 'Operation'
    },
    {
      term: 'Hall Station',
      definition: 'The Hall Station refers to the panel located outside the elevator doorway in the hallway that houses the call button.',
      category: 'Operation'
    },
    {
      term: 'Hoistway',
      definition: 'The opening (shaft) in which the elevator travels.',
      category: 'Structural'
    },
    {
      term: 'Hoistway Door',
      definition: 'The door that gives you access to the elevator.',
      category: 'Structural'
    },
    {
      term: 'Hoisting Ropes',
      definition: 'Dependent upon lift speed, rated load, number of floors served, etc. between four and six ropes are used together. Each rope is made up of a steel or fiber core surrounded by a number of strands which are in turn made up of a king wire surrounded by layers of wire.',
      category: 'Structural'
    },
    {
      term: 'Hydraulic Elevator',
      definition: 'An elevator moved by a fluid under pressure, acting upon a piston.',
      category: 'Structural'
    },
    {
      term: 'Hydraulic (Roped)',
      definition: 'This drive system utilizes a hydraulic jack and a wire rope to raise and lower the passenger car.',
      category: 'Structural'
    },
    {
      term: 'Independent Service',
      definition: 'Describes a type of operation used with an automatic elevator. Made available by a key operated switch which bypasses all landing calls and de-activates hall lanterns to enable sole operation of the car from its station panel.',
      category: 'Operation'
    },
    {
      term: 'Isolation Pads',
      definition: 'Rubber pads located between machine beams and the building structure to minimize the transmission of noise and vibration.',
      category: 'Structural'
    },
    {
      term: 'Interlock',
      definition: 'A device that provides a physical lock for hoistway doors and gives an electrical signal which allows the car to run.',
      category: 'Safety'
    },
    {
      term: 'Jack',
      definition: 'The Jack utilizes hydraulic power to lift or lower the passenger car.',
      category: 'Structural'
    },
    {
      term: 'Landing',
      definition: 'A term used to describe each floor on which the elevator will open.',
      category: 'Operation'
    },
    {
      term: 'Landing Zone',
      definition: 'A zone extending from a point 18" below an elevator to a point 18" above the landing.',
      category: 'Operation'
    },
    {
      term: 'Layout Drawing',
      definition: 'A scaled mechanical drawing showing dimensioned plan views and elevations of an elevator hoistway and machine room to indicate space conditions, pertinent dimensions, sizes and location of components of the installation.',
      category: 'Operation'
    },
    {
      term: 'Leveling',
      definition: 'The movement of an elevator toward the landing sill when it is within the leveling zone. When the word leveling is used, the inference is that the process of attaining a level stop or position (the platform level with the landing sill) is performed completely automatically.',
      category: 'Operation'
    },
    {
      term: 'Leveling Zone',
      definition: 'The limited distance above or below an elevator or material lift landing within which the leveling device is permitted to cause movement of the car toward the landing.',
      category: 'Operation'
    },
    {
      term: 'Load',
      definition: 'Capacity rating in pounds which an elevator is designed to safely handle.',
      category: 'Structural'
    },
    {
      term: 'Machine Beams',
      definition: 'Two or more horizontal steel beams which support the hoist machine. The prime function of machine beams is to carry the load of the hoist machine, elevator car and counterweight. They are not considered a structural part of a building.',
      category: 'Structural'
    },
    {
      term: 'Machine Room',
      definition: 'The room in which the power machinery for operation of the elevator is located.',
      category: 'Structural'
    },
    {
      term: 'Machine Room-less',
      definition: 'Used when located on top of the rail in the hoistway and the Control box is located nearby but does not require a separate room.',
      category: 'Structural'
    },
    {
      term: 'Muntz',
      definition: 'The name given to the bronze tone finish on the hall stations and car operating panel.',
      category: 'Operation'
    },
    {
      term: 'Nudging',
      definition: 'A system used with automatic door operation which, if the door remains open more than a predetermined time, will sound a warning signal and close the doors at a reduced speed and torque.',
      category: 'Safety'
    },
    {
      term: 'Operation – Constant Pressure',
      definition: 'Constant Pressure control systems are exactly as they are described. In order for the elevator to move, the button must have pressure maintained throughout operation. Once pressure is removed from the button, the elevator will stop. This type of operation allows the user to stop mid-travel and also change direction prior to arriving at a floor.',
      category: 'Operation'
    },
    {
      term: 'Operation – Momentary Pressure or Full Automatic',
      definition: 'A simple form of elevator operation that accepts only one call at a time, remembers that call and dispatches the car in the proper direction.',
      category: 'Operation'
    },
    {
      term: 'Operation – Selective Collective',
      definition: 'This is how most passenger elevators everyone is familiar with works. You press the button for the floor you want to go to, others press the floors they want to go to, and the elevator stops at each floor in order of other ascending or descending order.',
      category: 'Operation'
    },
    {
      term: 'Operation – Single Automatic',
      definition: 'There is one button for each landing served and one button on each floor served. This performs one function at a time. When the button is pressed for one landing, the elevator will go to that landing and then wait for its next command. (There is no collective function)',
      category: 'Operation'
    },
    {
      term: 'Overhead Machine',
      definition: 'The power unit which applies the energy necessary to raise and lower an elevator.',
      category: 'Structural'
    },
    {
      term: 'Overtravel Limit Switch',
      definition: 'Operates after direction limit switch to open the safety circuit and prevent the car from moving in either direction. Resetting of this switch can only be accomplished by a service technician.',
      category: 'Safety'
    },
    {
      term: 'Parking',
      definition: 'A feature incorporated into the signal system of an elevator or elevators by which and elevator receives a signal to always return to a preselected landing after all its car or landing signals have been answered and canceled.',
      category: 'Operation'
    },
    {
      term: 'Pit',
      definition: 'The pit provides clearance for the support components that are below the floor of the elevator car allowing the elevator floor to level with the lowest landing floor.',
      category: 'Structural'
    },
    {
      term: 'Power Door Operator',
      definition: 'A motor driven device mounted on the car which provides the motive force to open and close the car and landing doors.',
      category: 'Mechanical'
    },
    {
      term: 'Rail',
      definition: 'The structural member fastened to the walls of a hoistway to guide the car and counterweight.',
      category: 'Mechanical'
    },
    {
      term: 'Rated Load',
      definition: 'The load the elevator is designed to lift at rated speed.',
      category: 'Mechanical'
    },
    {
      term: 'Rated Speed',
      definition: 'The elevator speed in the up direction with the rated load in the car.',
      category: 'Operation'
    },
    {
      term: 'Safety Circuit',
      definition: 'A portion of the elevator control circuitry that includes a number of mechanical switch contacts and relay contacts in series. Usually includes the final limits, emergency stop button, governor contacts, door lock switches and safety gear operated switch. The cause of operation of any of these contacts constitutes a possible hazardous operation of the elevator and therefore stops all elevator operation.',
      category: 'Safety'
    },
    {
      term: 'Safety Edges',
      definition: 'Aluminum bar fitted to the car door leading vertical edge. Affording passenger protection from power operated doors. The mechanism houses switch contacts which operate when the bar comes into contact with an obstruction, stops the closing doors reversing them into the open position.',
      category: 'Safety'
    },
    {
      term: 'Seismic Switch',
      definition: 'Earthquake safety switches that disable elevators, which may otherwise become unsafe or unstable during an earthquake.',
      category: 'Safety'
    },
    {
      term: 'Service Disconnect',
      definition: 'Located near the controller in the machine room, the service disconnect is the main power switch to the elevator.',
      category: 'Electrical'
    },
    {
      term: 'Sheave',
      definition: 'A wheel mounted on bearings and having a number of grooves over which a rope or ropes may pass.',
      category: 'Mechanical'
    },
    {
      term: 'Simplex Operation',
      definition: 'Controller operation that involves only one car.',
      category: 'Operation'
    },
    {
      term: 'Slack Rope Switch',
      definition: 'All "hoisting rope" applications are fitted with a slack rope switch which when actuated "opens" the safety circuit stopping the elevator from operating. If slack appears in one or more ropes, the electrical switch is operated.',
      category: 'Safety'
    },
    {
      term: 'Sling',
      definition: 'The sling is an L-shaped steel support that holds the elevator car and is attached to the rail system.',
      category: 'Mechanical'
    },
    {
      term: 'Traction',
      definition: 'A method by which an elevator is moved, whereby the elevator is "pulled" up by cables; traction refers to the friction developed between the hoist ropes and drive sheave.',
      category: 'Mechanical'
    },
    {
      term: 'Traction Drive',
      definition: 'An electric machine in which the friction between the hoist ropes and the machine sheave is used to move the elevator car with the cable.',
      category: 'Mechanical'
    },
    {
      term: 'Underslung Car',
      definition: 'Refers to a method of roping an elevator car whereby the hoist ropes are passed under two sheaves mounted underneath the car. This car roping arrangement generally accompanies a basement drive machine installation in buildings of restricted height.',
      category: 'Mechanical'
    },
    {
      term: 'Winding Drum Elevator',
      definition: 'This drive system utilizes wire ropes that wind around a drum to raise and lower the passenger car.',
      category: 'Mechanical'
    }
  ];

  // Get unique categories
  const categories = ['all', 'Electrical', 'Structural', 'Power', 'Cabs & Gates'];

  // Get alphabet letters that have terms
  const availableLetters = useMemo(() => {
    const letters = new Set(terminologyData.map(item => item.term.charAt(0).toUpperCase()));
    return ['all', ...Array.from(letters).sort()];
  }, []);

  // Filter terminology based on search, category, and letter
  const filteredTerminology = useMemo(() => {
    return terminologyData
      .filter(item => {
        const matchesSearch = searchQuery === '' || 
          item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.definition.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        
        const matchesLetter = selectedLetter === 'all' || 
          item.term.charAt(0).toUpperCase() === selectedLetter;
        
        return matchesSearch && matchesCategory && matchesLetter;
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [searchQuery, selectedCategory, selectedLetter, terminologyData]);

  // Group filtered terms by first letter
  const groupedTerminology = useMemo(() => {
    const grouped: { [key: string]: TermEntry[] } = {};
    filteredTerminology.forEach(item => {
      const firstLetter = item.term.charAt(0).toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(item);
    });
    return grouped;
  }, [filteredTerminology]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Hidden on print */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 print:hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Elevator Terminology
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              This reference guide is designed to demystify the language of elevators and provide you with a clear understanding 
              of the terminology used in elevator maintenance. This glossary will equip you with the knowledge to navigate the 
              complexities of elevator terminology with confidence.
            </p>
          </div>

          {/* Search and Filter Controls - Hidden on print */}
          <div className="mt-12 space-y-6">
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search terminology..."
                className="w-full px-6 py-4 pl-14 text-lg border-2 border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
              <Icon
                name="MagnifyingGlassIcon"
                size={24}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900"
                >
                  <Icon name="XMarkIcon" size={20} />
                </button>
              )}
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 text-sm font-semibold border-2 transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-gray-900 text-white border-gray-900' :'bg-white text-gray-900 border-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {category === 'all' ? 'All Categories' : category}
                </button>
              ))}
            </div>

            {/* Alphabetical Navigation */}
            <div className="flex flex-wrap justify-center gap-2">
              {availableLetters.map((letter) => (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(letter)}
                  className={`w-10 h-10 text-sm font-bold border-2 transition-all duration-300 ${
                    selectedLetter === letter
                      ? 'bg-gray-900 text-white border-gray-900' :'bg-white text-gray-900 border-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {letter === 'all' ? '•' : letter}
                </button>
              ))}
            </div>

            {/* Print Button */}
            <div className="flex justify-center">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-8 py-3 bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors duration-300"
              >
                <Icon name="PrinterIcon" size={20} />
                Print Reference Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Print Header - Only visible on print */}
      <div className="hidden print:block px-8 py-6 border-b-2 border-gray-900">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">ELEVATOR TERMINOLOGY</h1>
        <p className="text-sm text-gray-600">
          Reference Guide | CR Elevator | Professional Elevator Components & Services
        </p>
      </div>

      {/* Results Count - Hidden on print */}
      {(searchQuery || selectedCategory !== 'all' || selectedLetter !== 'all') && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 print:hidden">
          <p className="text-gray-600">
            Showing <span className="font-semibold">{filteredTerminology.length}</span> {filteredTerminology.length === 1 ? 'term' : 'terms'}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>
      )}

      {/* Terminology Content */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8 print:px-8 print:py-6">
        <div className="max-w-7xl mx-auto">
          {filteredTerminology.length === 0 ? (
            <div className="text-center py-16 print:hidden">
              <Icon name="ExclamationCircleIcon" size={64} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No terms found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-12 print:space-y-8">
              {Object.keys(groupedTerminology)
                .sort()
                .map((letter) => (
                  <div key={letter} className="print:break-inside-avoid">
                    {/* Letter Header */}
                    <div className="mb-6 pb-4 border-b-4 border-gray-900">
                      <h2 className="text-4xl font-bold text-gray-900 print:text-2xl">
                        {letter}
                      </h2>
                    </div>

                    {/* Terms under this letter */}
                    <div className="space-y-8 print:space-y-6">
                      {groupedTerminology[letter].map((item, index) => (
                        <div
                          key={`${item.term}-${index}`}
                          className="pb-6 border-b border-gray-200 last:border-0 print:break-inside-avoid print:pb-4"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="text-2xl font-bold text-gray-900 print:text-lg">
                              {item.term}
                            </h3>
                            <span className="ml-4 px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-300 print:text-[10px]">
                              {item.category}
                            </span>
                          </div>
                          <p className="text-lg text-gray-700 leading-relaxed print:text-sm print:leading-normal">
                            {item.definition}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Print Footer - Only visible on print */}
      <div className="hidden print:block border-t-2 border-gray-900 px-8 py-4 mt-8">
        <div className="flex justify-between items-center text-xs text-gray-600">
          <p>© 2025 CR Elevator | All Rights Reserved</p>
          <p>For more resources, visit www.crelevator.com/resources</p>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          @page {
            size: letter;
            margin: 0.75in 0.5in;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:block {
            display: block !important;
          }
          
          .print\\:break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          .print\\:px-8 {
            padding-left: 2rem;
            padding-right: 2rem;
          }
          
          .print\\:py-6 {
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
          }
          
          .print\\:py-4 {
            padding-top: 1rem;
            padding-bottom: 1rem;
          }
          
          .print\\:pb-4 {
            padding-bottom: 1rem;
          }
          
          .print\\:space-y-8 > * + * {
            margin-top: 2rem;
          }
          
          .print\\:space-y-6 > * + * {
            margin-top: 1.5rem;
          }
          
          .print\\:text-2xl {
            font-size: 1.5rem;
            line-height: 2rem;
          }
          
          .print\\:text-lg {
            font-size: 1.125rem;
            line-height: 1.75rem;
          }
          
          .print\\:text-sm {
            font-size: 0.875rem;
          }
          
          .print\\:text-\\[10px\\] {
            font-size: 10px;
          }
          
          .print\\:leading-normal {
            line-height: 1.5;
          }
        }
      `}</style>
    </div>
  );
}